// btw-front/src/components/sections/Hero.jsx
import React from "react";
import Container from "../ui/Container";
import { Button } from "../ui/Button";
import { Fade } from "react-awesome-reveal";
import { useTranslation } from "react-i18next";

export default function Hero() {
  const { t } = useTranslation();

  return (
    <section className="relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 -z-50">
        <img className="h-full w-full object-cover" src="/images/background.png" alt="" />
        {/* 전체 톤 정리(너무 어둡지 않게) */}
        <div className="absolute inset-0 bg-slate-900/25" />
      </div>

      {/* Left readability gradient (왼쪽만 더 어둡게) */}
      <div className="pointer-events-none absolute inset-0 -z-40 bg-gradient-to-r from-slate-950/70 via-slate-950/25 to-transparent" />

      {/* Grid (너무 튀지 않게) */}
      <div className="absolute inset-0 -z-30 btw-grid-bg opacity-[0.07]" />

      {/* Glows (과하면 촌스러워져서 약하게) */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -z-20 h-[560px] w-[560px] -translate-x-1/2 rounded-full bg-cyan-400/10 blur-3xl" />
      <div className="pointer-events-none absolute top-24 -left-24 -z-20 h-[420px] w-[420px] rounded-full bg-blue-400/10 blur-3xl" />
      <div className="pointer-events-none absolute top-40 -right-28 -z-20 h-[460px] w-[460px] rounded-full bg-indigo-400/10 blur-3xl" />

      <Container className="relative py-16 md:py-24 lg:py-28 xl:py-36">
        {/* 오른쪽 패널이 너무 커 보이면 비율 조정 */}
        <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
          {/* Left */}
          <div className="min-w-0">
            <Fade direction="up" triggerOnce>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium text-white/90 backdrop-blur">
                <span
                  aria-hidden="true"
                  className="inline-block h-2 w-2 rounded-full bg-accent motion-safe:animate-pulse"
                />
                {t("hero.eyebrow")}
              </span>

              <h1 className="mt-5 sm:min-w-[420px] whitespace-pre text-[clamp(1.25rem,4.2vw,3.25rem)] font-bold leading-tight tracking-tight bg-gradient-to-b from-white to-white/80 bg-clip-text text-transparent drop-shadow-[0_10px_30px_rgba(0,0,0,0.55)]">
                {t("hero.title")}
              </h1>

              <p className="mt-6 max-w-xl whitespace-pre-line text-base leading-relaxed text-white/85 md:text-lg">
                {t("hero.desc")}
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Button variant="accent" href="#contact" className="btw-btn-lg group">
                  {t("hero.cta1")}
                  <span
                    aria-hidden="true"
                    className="ml-1 transition-transform group-hover:translate-x-0.5 motion-reduce:hidden"
                  >
                    →
                  </span>
                </Button>
                <Button variant="ghost-invert" href="#services" className="btw-btn-lg">
                  {t("hero.cta2")}
                </Button>
              </div>

              <div className="mt-10 flex flex-wrap gap-3 text-xs">
                {["Web · Mobile", "Backend · API", "CI/CD · Docker", "OpenSource"].map((txt) => (
                  <span
                    key={txt}
                    className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1 text-white/80 backdrop-blur"
                  >
                    {txt}
                  </span>
                ))}
              </div>
            </Fade>
          </div>

          {/* Right panel (✅ 통일된 Glass + 내부는 White cards) */}
          <Fade direction="up" delay={80} triggerOnce>
            <div className="relative min-w-0">
              {/* 패널 뒤쪽에 얇은 테두리/광택(전문가 느낌) */}
              <div className="pointer-events-none absolute -inset-1 rounded-[28px] bg-gradient-to-b from-white/15 to-white/5" />
              <div className="relative rounded-[26px] border border-white/15 bg-slate-950/25 p-6 shadow-[0_30px_90px_rgba(0,0,0,0.45)] backdrop-blur-2xl md:p-8">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="inline-block h-4 w-1 rounded-full bg-accent"
                    />
                    <div className="text-sm font-semibold tracking-wider text-white">
                      {t("hero.techTitle")}
                    </div>
                  </div>
                  <span className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-2 py-0.5 text-[10px] font-medium text-white/70 backdrop-blur">
                    {t("hero.version")}
                  </span>
                </div>

                {/* ✅ “주요 기술 분야” 카드 3개는 화이트 카드로 통일 */}
                <div className="mt-6 space-y-4">
                  <Metric
                    label={t("hero.m1Label")}
                    value={t("hero.m1Value")}
                    hint={t("hero.m1Hint")}
                    featured
                  />
                  <Metric
                    label={t("hero.m2Label")}
                    value={t("hero.m2Value")}
                    hint={t("hero.m2Hint")}
                  />
                  <Metric
                    label={t("hero.m3Label")}
                    value={t("hero.m3Value")}
                    hint={t("hero.m3Hint")}
                  />
                </div>

                {/* ✅ 대표 작업: 얇은 틴트 카드(화이트 X), 글자 대비 확실 */}
                <div className="mt-8 rounded-2xl border border-white/15 bg-slate-900/40 p-5 backdrop-blur-xl">
                  <div className="text-sm font-semibold text-white">{t("hero.workTitle")}</div>

                  <ul className="mt-3 space-y-2 text-sm text-white/80">
                    <li className="flex items-center justify-between gap-3">
                      <span className="truncate">{t("hero.w1")}</span>
                      <span className="shrink-0 text-xs text-white/60">CertBear</span>
                    </li>
                    <li className="flex items-center justify-between gap-3">
                      <span className="truncate">{t("hero.w2")}</span>
                      <span className="shrink-0 text-xs text-white/60">JEUS · WebtoB</span>
                    </li>
                    <li className="flex items-center justify-between gap-3">
                      <span className="truncate">{t("hero.w3")}</span>
                      <span className="shrink-0 text-xs text-white/60">UI/UX</span>
                    </li>
                    <li className="flex items-center justify-between gap-3">
                      <span className="truncate">{t("hero.w4")}</span>
                      <span className="shrink-0 text-xs text-white/60">React · Next.js</span>
                    </li>
                    <li className="flex items-center justify-between gap-3">
                      <span className="truncate">{t("hero.w5")}</span>
                      <span className="shrink-0 text-xs text-white/60">
                        Spring · Node.js · Java
                      </span>
                    </li>
                  </ul>
                </div>

                <p className="mt-4 text-xs text-white/55">{t("hero.note")}</p>
              </div>
            </div>
          </Fade>
        </div>
      </Container>
    </section>
  );
}

function Metric({ label, value, hint, featured = false }) {
  return (
    <div
      className={`flex min-h-[76px] items-center justify-between gap-3 rounded-2xl border bg-white/85 p-4 shadow-[0_12px_35px_rgba(0,0,0,0.18)] backdrop-blur-sm ${featured ? "border-accent/40 ring-1 ring-accent/30" : "border-white/20"}`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 text-xs tracking-wider text-slate-500">
          {featured && (
            <span
              aria-hidden="true"
              className="inline-block h-1.5 w-1.5 rounded-full bg-accent motion-safe:animate-pulse"
            />
          )}
          {label}
        </div>
        <div className="mt-1 break-keep text-sm font-semibold leading-snug text-slate-900 sm:text-base lg:text-lg">
          {value}
        </div>
      </div>
      <div className="shrink-0 max-w-[42%] break-keep text-right text-[11px] leading-snug text-slate-500">
        {hint}
      </div>
    </div>
  );
}
